/**
 * /terms, /privacy, /refunds, /dpa and /contact.
 *
 * Written for how RareMargin is actually sold and run (2 November 2026): an individual in India sells it to businesses
 * worldwide; Dodo Payments resells each subscription as merchant of record; the app runs on Cloudflare, the database
 * on Neon (Databricks) in the United States, the assistant on OpenAI's API; support mail is forwarded to Gmail.
 * Researched against India's IT Act 2000 and SPDI Rules 2011 (privacy policy, Grievance Officer), the DPDP Act 2023 and
 * DPDP Rules 2025 (itemised notice, rights, Data Protection Board), the GDPR and UK GDPR (Articles 13, 28 and 46; the
 * 2021 standard contractual clauses and the UK addendum), US state privacy law, the EU AI Act's Article 50 (telling
 * people they are talking to an AI, in force since 2 August 2026), and Dodo Payments' Buyer Terms and website checks.
 * Not legal advice; when the business or its providers change, these pages must change with them.
 *
 * The seller's legal name, address and court city come from the server (LEGAL_NAME, BUSINESS_ADDRESS, LEGAL_CITY),
 * which refuses to start in production without them. In development the gaps are highlighted.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from '../components/ui';
import { Band, Eyebrow, SiteButton, SiteLayout } from './chrome';
import { LEGAL_UPDATED, PAGES } from './content';
import { siteOrigin, usePublicPlan, useSite } from './hooks';

/** "3 November 2026", from the date the sitemap and the pages' structured data give. */
const UPDATED = new Date(`${LEGAL_UPDATED}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

/** A setting the server hasn't been given yet (development only); highlighted so it can't be missed. */
const Fill = ({ children }: { children: ReactNode }) => <span className="site-mark rounded-[3px] px-1 font-plex text-[0.86em]">[{children}]</span>;
const A = ({ to, children }: { to: string; children: ReactNode }) => (
  <Link to={to} className="text-site-green underline-offset-4 hover:underline">
    {children}
  </Link>
);
const Ext = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noreferrer" className="text-site-green underline-offset-4 hover:underline">
    {children}
  </a>
);

interface Section {
  id: string;
  title: string;
  body: ReactNode;
}

function LegalPage({ title, intro, sections }: { title: string; intro: ReactNode; sections: Section[] }) {
  const site = useSite().data;
  const missing = site && (!site.legalName || !site.businessAddress || !site.legalCity);
  return (
    <SiteLayout title={`${title} · RareMargin`}>
      <Band stage="soft" inner="!pt-14 lg:!pt-20">
        {missing && (
          <div role="note" className="mb-12 flex gap-3 rounded-[16px] bg-site-close px-5 py-4 text-[14.5px] leading-relaxed text-site-close-ink ring-1 ring-site-close-2">
            <span className="site-eyebrow mt-1 shrink-0">Setup</span>
            <span>This page names the seller from the server’s settings. Set LEGAL_NAME, BUSINESS_ADDRESS and LEGAL_CITY to fill in the highlighted gaps; production won’t start without them.</span>
          </div>
        )}
        <div className="grid gap-12 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-16">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <Eyebrow>Legal</Eyebrow>
            <h1 className="mt-5 font-display text-[clamp(36px,4vw,52px)] leading-[1] font-[580] tracking-[-0.04em]">{title}</h1>
            <div className="site-eyebrow mt-5 text-site-muted">
              Updated <time dateTime={LEGAL_UPDATED}>{UPDATED}</time>
            </div>
            <nav aria-label="On this page" className="mt-8 hidden max-h-[calc(100vh-18rem)] overflow-y-auto border-t border-site-rule pt-5 lg:block">
              <ol className="space-y-2 text-[14px]">
                {sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="flex gap-3 text-site-ink-2 hover:text-site-ink">
                      <span className="font-plex text-[12px] text-site-muted">{String(i + 1).padStart(2, '0')}</span>
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>
          <article className="max-w-[70ch]">
            <div className="space-y-4 text-[17px] leading-relaxed text-site-ink-2">{intro}</div>
            {sections.map((s, i) => (
              <section key={s.id} id={s.id} className="mt-12 scroll-mt-24 border-t border-site-rule pt-8">
                <h2 className="flex items-baseline gap-4 font-display text-[22px] font-[600] tracking-[-0.025em]">
                  <span className="font-plex text-[13px] font-normal text-site-muted">{String(i + 1).padStart(2, '0')}</span>
                  {s.title}
                </h2>
                <div className="legal-body mt-4 space-y-4 text-[16px] leading-[1.7] text-site-ink-2 [&_b]:font-[620] [&_b]:text-site-ink [&_li]:pl-1 [&_ol]:list-decimal [&_ol]:space-y-2 [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5">
                  {s.body}
                </div>
              </section>
            ))}
          </article>
        </div>
      </Band>
    </SiteLayout>
  );
}

/** Rows of two or three columns, for the data, provider and retention lists. No `head`, no header row. */
function Rows({ head, rows }: { head?: string[]; rows: ReactNode[][] }) {
  return (
    <div className="overflow-x-auto rounded-[14px] ring-1 ring-site-rule">
      <table className="w-full border-collapse text-left text-[14.5px] leading-snug">
        {head && (
          <thead>
            <tr className="bg-site-oat">
              {head.map((h) => (
                <th key={h} className="site-eyebrow px-4 py-3 align-bottom font-normal text-site-muted">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={cx('align-top', (head || i > 0) && 'border-t border-site-rule')}>
              {r.map((c, j) => (
                <td key={j} className={cx('px-4 py-3', j === 0 && 'font-[600] text-site-ink')}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Who sells the service and how to reach them, from the server's settings. */
function useParties() {
  const site = useSite().data;
  const trialDays = usePublicPlan().data?.trialDays ?? 14;
  const brand = site?.businessName ?? 'RareMargin';
  const seller = site?.legalName ? <b>{site.legalName}</b> : <Fill>LEGAL_NAME</Fill>;
  const address = site?.businessAddress || <Fill>BUSINESS_ADDRESS</Fill>;
  const city = site?.legalCity || <Fill>LEGAL_CITY</Fill>;
  const email = site ? (
    <a href={`mailto:${site.supportEmail}`} className="text-site-green underline-offset-4 hover:underline">
      {site.supportEmail}
    </a>
  ) : (
    'our support address'
  );
  return { brand, seller, address, city, email, trialDays };
}

/** Everyone who handles personal data for us. Shared by the privacy policy and the data processing terms. */
function Subprocessors() {
  return (
    <Rows
      head={['Provider', 'What it does', 'Where']}
      rows={[
        [
          'Cloudflare, Inc.',
          'Runs the app and its network: hosting (Workers and Containers), protection from attacks, and sending and forwarding our email. All traffic to the service passes through it.',
          'Its global network; a company in the United States',
        ],
        ['Databricks, Inc. (Neon)', 'The database that stores accounts and books, with its backups.', 'Amazon Web Services, United States (US East)'],
        [
          'OpenAI, L.L.C.',
          'The AI model behind the assistant and AI-built dashboards. Receives your question and the parts of your books the assistant reads to answer it. Not used to train models; kept up to 30 days for abuse monitoring.',
          'United States',
        ],
        ['Google LLC (Gmail)', 'The inbox our support, billing and other addresses forward to: the emails you send us.', 'United States'],
        [
          'Dodo Payments',
          'Merchant of record: checkout, payment, invoices, sales tax and refunds. It decides what it collects at checkout under its own privacy policy, as an independent controller.',
          'Where Dodo Payments and its payment partners operate',
        ],
      ]}
    />
  );
}

// --- Terms of service ---------------------------------------------------------------------------------------------

export function TermsPage() {
  const { brand, seller, address, city, email, trialDays } = useParties();
  return (
    <LegalPage
      title="Terms of service"
      intro={
        <>
          <p>
            These terms are an agreement between {seller}, an individual based in India who runs {brand} (“we”, “us”), and the business that creates or uses a company on {brand} at {siteOrigin()} (“you”). They
            cover the software, the website, the API and the assistant (together, the “service”).
          </p>
          <p>
            By creating a company, accepting an invitation or using the service, you agree to these terms on behalf of your business, together with the <A to="/privacy">privacy policy</A>, the{' '}
            <A to="/refunds">refund policy</A> and the <A to="/dpa">data processing terms</A>. If you don’t agree, don’t use the service.
          </p>
        </>
      }
      sections={[
        {
          id: 'business',
          title: 'A service for businesses',
          body: (
            <>
              <p>
                {brand} is accounting software for businesses. You use it in the course of your trade or profession, not as a consumer. The person who accepts these terms must be at least 18 and allowed to
                bind the business.
              </p>
              <p>
                Subscriptions are bought from <b>Dodo Payments</b>, which resells the service as merchant of record. Your purchase is a contract with Dodo Payments under its{' '}
                <Ext href="https://dodopayments.com/legal/buyer-terms">Buyer Terms</Ext>; your use of the service is governed by these terms, which are the licence those Buyer Terms refer to. If the two
                disagree about payment, tax or the checkout, the Buyer Terms apply; on everything else, these terms do.
              </p>
            </>
          ),
        },
        {
          id: 'accounts',
          title: 'Accounts and access',
          body: (
            <ul>
              <li>Give accurate details, keep them current and confirm your email address when asked. Some features, such as the assistant and invitations, wait for a confirmed address.</li>
              <li>
                Keep passwords, recovery codes and API keys secret. We recommend turning on two-factor sign-in. You are responsible for everything done under your company, including by the people, roles
                and keys you add.
              </li>
              <li>Tell us at {email} straight away if you think an account or key has been misused.</li>
              <li>An owner controls the company: who belongs to it, their roles, billing, exports and deletion.</li>
            </ul>
          ),
        },
        {
          id: 'trial',
          title: 'Free trial',
          body: (
            <p>
              Each new company gets a free trial of {trialDays} days, with no card needed and a smaller allowance of assistant messages. When the trial ends without a subscription, the company becomes
              read-only: you can still sign in, read and export everything, but not change your books. Nothing is deleted at that point.
            </p>
          ),
        },
        {
          id: 'billing',
          title: 'Subscription, price and tax',
          body: (
            <>
              <p>
                The price and its currency are shown on the <A to="/pricing">pricing page</A> and at checkout. It is charged in advance for each billing period by Dodo Payments, which issues the invoice
                and adds any sales tax, VAT or GST due where you are. Subscriptions renew automatically until cancelled.
              </p>
              <p>
                If a payment fails, Dodo Payments retries it and the company keeps working for a short grace period. If it still can’t be collected, the company becomes read-only until it is paid.
              </p>
              <p>
                We may change the price with at least 30 days’ notice by email to the company’s owners. A new price applies from your next billing period after the notice ends; if you don’t accept it, cancel
                before then.
              </p>
            </>
          ),
        },
        {
          id: 'cancel',
          title: 'Cancelling and refunds',
          body: (
            <p>
              An owner or admin can cancel at any time from Settings → Billing → Manage billing, or by writing to {email}. Access continues until the end of the period already paid for, and there are no
              further charges. Refunds, including a full refund within 14 days of your first payment, are set out in the <A to="/refunds">refund policy</A>. Please talk to us before disputing a charge
              with your bank.
            </p>
          ),
        },
        {
          id: 'your-data',
          title: 'Your data',
          body: (
            <>
              <p>
                The books and other content you put into the service (“your data”) stay yours. You give us permission to host, copy, process and display your data only to provide, secure and support the
                service for you and as these terms describe. We don’t sell your data, use it for advertising, or use it to train AI models, and we don’t let our providers do so. We may use counts of how
                the service is used, such as how many assistant messages a company sends, to run and improve it, never the contents of your books.
              </p>
              <p>
                You can download all of your company’s data as one file (Settings → Company), download reports and read your data through the API at any time, including while the company is read-only.
              </p>
              <p>
                An owner can delete the company from the same page. It is removed from the live service at once and from backups within 7 days. Accounts left with no other company are deleted with it.
              </p>
              <p>
                If a company stays without a subscription and nobody signs in to it for 12 months, we may delete it, after emailing its owners at least 30 days before.
              </p>
              <p>
                You are responsible for having the right to put personal data about others (customers, vendors, employees) into the service. When we process it for you, our{' '}
                <A to="/dpa">data processing terms</A> apply.
              </p>
            </>
          ),
        },
        {
          id: 'assistant',
          title: 'The AI assistant',
          body: (
            <>
              <p>
                The assistant and AI-built dashboards are an <b>AI system</b>: answers and drafts are generated by an AI model from OpenAI, and the app says so where you use it. They can be wrong or incomplete.
                Anything the assistant drafts that would change your books is a proposal that waits for a person to approve it. You are responsible for checking what you approve and rely on.
              </p>
              <p>
                To answer, the assistant sends your question and the parts of your books it reads to the AI provider, as described in the <A to="/privacy">privacy policy</A>. Don’t ask it to process
                data you aren’t allowed to share, or to do anything the acceptable use rules below forbid.
              </p>
              <p>Each company has a monthly allowance of assistant messages, shown on the pricing page and in Settings → Billing. Unused messages don’t carry over.</p>
            </>
          ),
        },
        {
          id: 'not-advice',
          title: 'Software, not professional advice',
          body: (
            <p>
              {brand} helps you keep books. It is not accounting, tax, audit, investment or legal advice, and it doesn’t replace a qualified professional. You remain responsible for your records, filings,
              tax returns and compliance with the laws that apply to your business.
            </p>
          ),
        },
        {
          id: 'use',
          title: 'Acceptable use',
          body: (
            <>
              <p>You must not, and must not let anyone using your company:</p>
              <ul>
                <li>use the service for anything unlawful, fraudulent or deceptive, including recording transactions you know to be false or hiding money from authorities;</li>
                <li>try to reach other companies’ data, probe, scan or break our security, or get around limits, the paywall, roles or approvals;</li>
                <li>overload the service, scrape it outside the API, or reverse engineer it, except where the law allows despite this term;</li>
                <li>upload malware, or content you have no right to use;</li>
                <li>use the assistant to produce harmful, abusive or illegal content, or in ways the AI provider’s usage policies forbid;</li>
                <li>resell or sublicense the service, or use it to build a competing product, without our written agreement.</li>
              </ul>
            </>
          ),
        },
        {
          id: 'api',
          title: 'API, MCP and webhooks',
          body: (
            <p>
              API keys act for your company with the role you give them; keep them secret and revoke any you no longer need. AI apps you connect by signing in, such as Claude or ChatGPT, act for you
              with the role you choose, never more than your own, until you disconnect them in Settings. We may limit the rate of requests to protect the service. Webhooks, API clients and connected
              apps send your data to the places you choose, and you are responsible for those places. We aim to keep the API stable and to give at least 30 days’ notice of changes that break it, unless
              security or the law requires a faster change.
            </p>
          ),
        },
        {
          id: 'ours',
          title: 'Our software and your feedback',
          body: (
            <p>
              We own the service, its software, design and the {brand} name. While you have access, we give you a non-exclusive, non-transferable right to use it for your business under these terms; no other
              rights pass to you. If you send us ideas or feedback, we may use them freely without owing you anything.
            </p>
          ),
        },
        {
          id: 'confidential',
          title: 'Confidentiality',
          body: (
            <p>
              Each of us will keep the other’s non-public information confidential and use it only for this agreement. Your data is your confidential information. This doesn’t cover information that is
              public through no fault of the receiver, already known to it, or independently developed. Either of us may disclose information when the law or a court requires it, after telling the other
              first where the law allows.
            </p>
          ),
        },
        {
          id: 'availability',
          title: 'Availability, support and changes',
          body: (
            <>
              <p>
                We work to keep the service available and your data safe, but we don’t promise it will be uninterrupted or error-free, and there is no uptime guarantee. We may need short maintenance
                windows.
              </p>
              <p>Support is by email at {email}. We aim to reply within two business days (India).</p>
              <p>
                We may improve, change or remove features. If a change takes away something you materially rely on, we will tell you ahead of time where we reasonably can, and if it makes the service
                substantially worse for you, you may cancel and ask for a pro-rata refund of the period you have paid for and not used.
              </p>
            </>
          ),
        },
        {
          id: 'third-parties',
          title: 'Providers and integrations',
          body: (
            <p>
              The service relies on providers such as Cloudflare, Databricks (Neon), OpenAI and Dodo Payments, listed in the <A to="/privacy#providers">privacy policy</A>. We choose them with care and
              remain responsible for our own obligations to you, but we aren’t responsible for services you connect yourself, such as your webhook endpoints, API clients or bank files.
            </p>
          ),
        },
        {
          id: 'warranty',
          title: 'Disclaimers',
          body: (
            <p>
              Apart from what these terms expressly promise, the service is provided “as is” and “as available”. To the extent the law allows, we disclaim all other warranties, express or implied, including
              merchantability, fitness for a particular purpose and non-infringement.
            </p>
          ),
        },
        {
          id: 'liability',
          title: 'Limitation of liability',
          body: (
            <>
              <p>To the extent the law allows:</p>
              <ul>
                <li>
                  neither of us is liable for indirect, incidental, special or consequential losses, or for lost profits, revenue, goodwill or data (restoring data from backups we hold aside), even if
                  warned they were possible;
                </li>
                <li>
                  each party’s total liability for all claims under or about this agreement is limited to the amounts you paid for the service in the 12 months before the event giving rise to the claim,
                  or 100 US dollars if that is more.
                </li>
              </ul>
              <p>
                These limits don’t apply to your obligation to pay, your indemnity below, a breach of confidentiality, fraud, wilful misconduct, or any liability that the law doesn’t allow to be limited.
              </p>
            </>
          ),
        },
        {
          id: 'indemnity',
          title: 'Indemnity',
          body: (
            <p>
              You will defend and compensate us against claims, losses and reasonable costs brought by others that arise from your data, your use of the service in breach of these terms or the law, or the
              places you send data through webhooks or the API.
            </p>
          ),
        },
        {
          id: 'ending',
          title: 'Suspension and ending the agreement',
          body: (
            <>
              <p>You can stop using the service and cancel at any time.</p>
              <p>
                We may suspend access straight away if there is a security risk, unlawful use or a serious breach of the acceptable use rules. For other breaches we will tell you and, where it can be fixed,
                give you at least 14 days to fix it before we suspend or close the account.
              </p>
              <p>
                When the agreement ends, you keep read-only access to export your data for at least 30 days, unless the law or a serious breach stops us. After that, your data is handled as the{' '}
                <A to="/privacy#retention">privacy policy</A> describes. Sections that by their nature should survive (your data, confidentiality, liability, indemnity, law and disputes) survive.
              </p>
            </>
          ),
        },
        {
          id: 'law',
          title: 'Governing law and disputes',
          body: (
            <>
              <p>These terms are governed by the laws of India, without regard to conflict-of-laws rules. The UN Convention on Contracts for the International Sale of Goods doesn’t apply.</p>
              <p>
                If a dispute arises, write to us first: we will both try in good faith to settle it within 30 days. If it isn’t settled, it will be finally resolved by arbitration under the Arbitration and
                Conciliation Act, 1996, by a sole arbitrator appointed by agreement (or, failing agreement, as that Act provides). The seat and venue of arbitration is {city}, India; hearings may be held
                online, and the language is English.
              </p>
              <p>Subject to arbitration, the courts at {city}, India have exclusive jurisdiction. Either of us may ask any competent court for urgent interim relief.</p>
            </>
          ),
        },
        {
          id: 'general',
          title: 'General',
          body: (
            <ul>
              <li>
                <b>Changes to these terms:</b> if we change them in a way that matters, we will email the company’s owners at least 30 days before the change applies and update the date above. Changes needed
                by law or for security can apply sooner. Continuing to use the service after a change applies means you accept it; if you don’t, cancel before then.
              </li>
              <li>
                <b>Whole agreement:</b> these terms, with the privacy policy, refund policy and data processing terms, are the whole agreement about the service. If they conflict, the data processing terms
                win on personal data, then these terms.
              </li>
              <li>
                <b>Transfer:</b> you may not transfer this agreement without our consent. We may transfer it to someone who takes over the service, after telling you.
              </li>
              <li>
                <b>Events beyond control:</b> neither of us is liable for delays caused by events outside reasonable control, such as outages of the internet or of providers, natural disasters or government
                action.
              </li>
              <li>
                <b>Sanctions:</b> you confirm you are not located in, or controlled from, a country or by a person under comprehensive sanctions of India, the United Nations, the United States, the
                United Kingdom or the European Union, and won’t use the service for anyone who is.
              </li>
              <li>
                <b>Notices:</b> we send notices by email to the company’s owners; you send them to {email}.
              </li>
              <li>If a term can’t be enforced, the rest still applies. Not enforcing a term isn’t a waiver. We are independent parties. These terms are written in English, which prevails over any translation.</li>
            </ul>
          ),
        },
        {
          id: 'contact',
          title: 'Contact',
          body: (
            <p>
              {seller}, {address}. Email {email}.
            </p>
          ),
        },
      ]}
    />
  );
}

// --- Privacy policy -----------------------------------------------------------------------------------------------

export function PrivacyPage() {
  const { brand, seller, address, email } = useParties();
  return (
    <LegalPage
      title="Privacy policy"
      intro={
        <>
          <p>
            This policy explains what personal data {brand} collects, why, who else handles it, how long it is kept, and the rights you have. It is written to stand on its own, so you can read it without the
            terms.
          </p>
          <p>
            In short: we collect what is needed to run accounting software for your business, we don’t sell it, nothing in your books is used for advertising, and nobody trains AI on it. Our public
            website (not the app) uses Reddit’s ad pixel to see which visits come from our ads, and you can turn it off. We also count the pages and buttons people use, ourselves and without cookies,
            to improve the website and the app.
          </p>
        </>
      }
      sections={[
        {
          id: 'who',
          title: 'Who is responsible',
          body: (
            <>
              <p>
                {brand} is run by {seller}, an individual based in India, at {address}. Email {email}.
              </p>
              <p>We have two roles, depending on the data:</p>
              <ul>
                <li>
                  <b>Accounts, billing, support and website visits:</b> we decide how this data is used, so we are its controller (a “data fiduciary” under India’s Digital Personal Data Protection Act, 2023).
                </li>
                <li>
                  <b>A company’s books:</b> the customers, vendors, transactions and other records a business keeps in {brand}, including details about people. That business decides what goes in and is the
                  controller; we process it on its behalf under our <A to="/dpa">data processing terms</A>. If your details are in a company’s books, contact that company first.
                </li>
              </ul>
            </>
          ),
        },
        {
          id: 'collect',
          title: 'What we collect, and why',
          body: (
            <>
              <Rows
                head={['Data', 'Why we use it']}
                rows={[
                  [
                    'Account: name, work email, password (stored only as a salted scrypt hash), whether the email is confirmed, the companies you belong to and your role in each',
                    'To create and secure your account, sign you in, confirm your address and control what you can see and do.',
                  ],
                  [
                    'How you heard about us, if you answer the optional question at sign-up, and anything you add (such as what you asked an AI assistant)',
                    'To learn which channels bring people to us and how they describe what they need. It isn’t shown to anyone in your company.',
                  ],
                  [
                    'Two-factor sign-in, if you turn it on: the authenticator secret and your recovery codes (stored only as hashes)',
                    'To check the code you enter when you sign in.',
                  ],
                  ['Company: its name, base currency, settings and plan', 'To run the company’s books the way it chose.'],
                  [
                    'Books: accounts, entries, customers, vendors, contracts, invoices, bills, bank transactions and attachments you add or import',
                    'To provide the service to the company, which decides what goes in.',
                  ],
                  [
                    'Assistant: your questions, its answers, conversation threads, prompts for AI dashboards, and how many messages each person sends',
                    'To answer you, let you return to a thread, and keep to the company’s monthly allowance.',
                  ],
                  [
                    'Activity: who did what in a company and when (the audit log), API keys (stored only as hashes), webhook settings, and the AI apps people connect (the app’s name and address, the role, when it was connected and last used; its tokens only as hashes)',
                    'To keep a trustworthy record of changes, which accounting needs, and to run your integrations.',
                  ],
                  [
                    'Billing: Dodo Payments’ customer and subscription references, the plan’s status and dates, and the payment notices Dodo sends us (which include the buyer’s name, email, country and amount)',
                    'To know whether the company has paid, show billing in the app, and keep billing records. We never see full card numbers.',
                  ],
                  ['Emails: the ones we send you and the ones you send us', 'To deliver the messages the service needs (confirmations, resets, invitations, trial and billing notices) and to help you.'],
                  [
                    'Website visits, through Reddit’s ad pixel on the public pages and the sign-up page: the pages you view, the ad you came from, your IP address and browser details, and whether you signed up',
                    'To see which of our ads on Reddit bring visitors and sign-ups, and decide where to advertise. Never collected inside the app, or once you turn it off (see cookies).',
                  ],
                  [
                    'Usage, counted by us: on the website, the pages you open, the links and buttons you click, how long a page is on screen and how far down you read, where your visit came from (the referring site, or the tags on an ad’s link), your country and your device type and browser; inside the app, the screens you open and the buttons you press, with your account and company, never anything from the books',
                    'To see how people find and use the website and the app, and improve them. Only we see it. Visits are counted with a code made from your IP address and browser details and a value that changes every day, so the address itself isn’t stored and a visit can’t be traced back to you.',
                  ],
                  [
                    'Technical: IP address, browser details and times of requests',
                    'To keep the service secure and limit abuse, and to count visits (see usage). IP addresses are used in memory to slow down repeated sign-in attempts and appear in our host’s short-lived request logs.',
                  ],
                ]}
              />
              <p>
                You give us account details yourself when you sign up or accept an invitation; without them we can’t provide an account. We don’t collect special categories of data (such as health or
                religion), and we don’t ask for them. The service isn’t meant for anyone under 18, and we don’t knowingly collect their data.
              </p>
            </>
          ),
        },
        {
          id: 'legal-bases',
          title: 'Legal grounds',
          body: (
            <>
              <p>Where the GDPR or UK GDPR applies, we rely on:</p>
              <ul>
                <li>
                  <b>contract:</b> to provide the service you or your company signed up for (accounts, books, assistant, emails it needs);
                </li>
                <li>
                  <b>legitimate interests:</b> to keep the service secure, prevent fraud and abuse, keep audit records, answer you, improve the service from usage counts and learn how people find us
                  (the optional question at sign-up); we have weighed these against your rights, and you can object (see your rights);
                </li>
                <li>
                  <b>legal obligation:</b> to keep records or disclose data when the law requires;
                </li>
                <li>
                  <b>consent:</b> for Reddit’s ad pixel, which the website asks about before it runs; you can withdraw it at any time under “Your privacy choices” at the foot of the website.
                </li>
              </ul>
              <p>
                Under India’s DPDP Act, we process account data for the purpose you gave it to us for (section 7(a)) or with your consent given at sign-up, which you can withdraw by deleting your account.
                Withdrawing doesn’t affect processing done before.
              </p>
              <p>
                We don’t sell personal data, show advertising in the service, or make decisions about you by automated means that have legal or similarly significant effects. The only advertising
                technology we use is Reddit’s pixel on the public website (see cookies).
              </p>
            </>
          ),
        },
        {
          id: 'providers',
          title: 'Who else handles it',
          body: (
            <>
              <p>We share personal data only with these providers, each bound by a contract that limits what it can do with the data, and only as needed for what it does for us:</p>
              <Subprocessors />
              <p>Besides them, data goes:</p>
              <ul>
                <li>to the people in your company, according to the roles its owners assign;</li>
                <li>to the places your company tells us to send it, such as its webhook endpoints and API clients;</li>
                <li>to the AI apps a person connects, such as Claude or ChatGPT, which read what the role they were given allows and use it under their own terms and privacy policies;</li>
                <li>to Reddit, Inc., through its ad pixel on the public website if it runs for you (see cookies), which uses that data under its own privacy policy, including to show you ads;</li>
                <li>to authorities, courts or others when the law requires it, or to protect people from harm;</li>
                <li>to whoever takes over the service, if it is transferred, under this policy.</li>
              </ul>
              <p>We will update this list before adding a provider, and email account owners when a change matters.</p>
            </>
          ),
        },
        {
          id: 'transfers',
          title: 'Where data is processed',
          body: (
            <>
              <p>
                We run the service from India. The database is in the United States (Amazon Web Services, US East), Cloudflare serves requests from its data centres around the world, and OpenAI, Google and
                Reddit process data in the United States. So your data is transferred outside your country, including outside India and the European Economic Area.
              </p>
              <p>
                Where the GDPR or UK GDPR requires safeguards, transfers are covered by the European Commission’s standard contractual clauses and the UK addendum: in our providers’ data processing terms,
                and, for data your company sends us, in our own <A to="/dpa">data processing terms</A>. Under India’s DPDP Act, data may be transferred abroad except to countries the Government restricts.
              </p>
            </>
          ),
        },
        {
          id: 'retention',
          title: 'How long we keep it',
          body: (
            <Rows
              head={['Data', 'Kept']}
              rows={[
                [
                  'Account and the company’s books',
                  'While the account is open. When an owner deletes a company, it goes from the live service at once and from backups within 7 days, with every account left with no other company.',
                ],
                ['Companies without a subscription', 'Kept read-only so you can come back. After 12 months with no subscription and no sign-in, we may delete them, after emailing the owners 30 days ahead.'],
                ['Sign-in sessions', '14 days, or until you sign out. Changing your password or two-factor settings signs out your other sessions.'],
                ['Emailed links and two-factor steps', 'Until they expire (5 minutes to 7 days) or are used, then deleted within a day.'],
                [
                  'Connected AI apps',
                  'Until someone disconnects the app, the person who connected it leaves the company, or it goes 30 days unused, then deleted within a day. Its access lasts an hour at a time.',
                ],
                ['Assistant conversations', 'Until someone in the company deletes them, or the company is deleted. OpenAI keeps the requests up to 30 days for abuse monitoring.'],
                ['Request logs at our host', 'Up to 7 days.'],
                ['Usage counts (pages, clicks, screens)', '13 months, then deleted. The daily value behind the visit code is deleted after two days, so older visits can’t be matched to anyone.'],
                ['Reddit’s pixel', 'Its cookies last 90 days, or until you turn it off or clear them. Reddit keeps what it receives under its own privacy policy.'],
                ['Emails with us', 'As long as needed to help you, and no more than 3 years.'],
                ['Payment records', 'Dodo Payments keeps them as tax and payment laws require.'],
              ]}
            />
          ),
        },
        {
          id: 'security',
          title: 'Security',
          body: (
            <>
              <p>
                Data is encrypted in transit (HTTPS) and stored encrypted at rest by our database provider. Passwords, recovery codes, API keys, app tokens and emailed links are stored only as hashes. Sign-in
                attempts are rate limited, two-factor sign-in is available to everyone, and access inside a company follows the roles its owners assign. Posted entries are sealed into a tamper-evident hash chain,
                and changes proposed by the AI, by API clients or by connected apps wait for a person to approve them.
              </p>
              <p>
                No system is perfectly secure. If a breach affects your personal data, we will tell the affected companies and people without undue delay, with what happened and what to do, and notify the
                authorities the law requires (in India, the Data Protection Board).
              </p>
            </>
          ),
        },
        {
          id: 'rights',
          title: 'Your rights',
          body: (
            <>
              <p>Depending on where you live, you can ask us to:</p>
              <ul>
                <li>tell you what personal data we have about you and how we use it, and give you a copy (in a portable format where the GDPR applies);</li>
                <li>correct, complete or update it;</li>
                <li>delete it (owners can delete a company and its accounts themselves in Settings → Company);</li>
                <li>restrict or object to how we use it, including where we rely on legitimate interests;</li>
                <li>withdraw consent you gave, without affecting what was done before.</li>
              </ul>
              <p>
                In India you can also nominate someone to exercise these rights if you die or become unable to, and, after first raising a grievance with us, complain to the Data Protection Board of India.
                In the EU or UK you can complain to your data protection authority. Residents of US states with privacy laws have the rights those laws give. Reddit’s pixel on the public website may
                count as “sharing” personal data or “targeted advertising” under those laws: you can opt out under “Your privacy choices” at the foot of the website, and we treat a Global Privacy
                Control signal from your browser as an opt-out. We don’t sell personal data.
              </p>
              <p>
                To use a right, write to {email} from your account’s email address. It’s free. We may ask you to confirm who you are, and we reply within 30 days. For data in a company’s books, we pass
                your request to that company and help it respond.
              </p>
            </>
          ),
        },
        {
          id: 'grievance',
          title: 'Grievance Officer',
          body: (
            <>
              <p>As India’s IT Rules and DPDP Act require, our Grievance Officer handles complaints and questions about how we process personal data:</p>
              <p>
                {seller}, Grievance Officer, {brand}
                <br />
                {address}
                <br />
                {email}
              </p>
              <p>We acknowledge a grievance within 2 business days and resolve it within 30 days.</p>
            </>
          ),
        },
        {
          id: 'cookies',
          title: 'Cookies and browser storage',
          body: (
            <>
              <p>The service itself uses only what it needs:</p>
              <ul>
                <li>
                  <code className="font-plex text-[0.9em]">cn_session</code>: a cookie that keeps you signed in, for up to 14 days.
                </li>
                <li>
                  In your browser’s storage: whether you were signed in (<code className="font-plex text-[0.9em]">raremargin:signed-in</code>), whether you turned off the website’s animations, and whether you
                  hid a company’s getting-started card. On the public website, your choice about Reddit’s pixel (<code className="font-plex text-[0.9em]">raremargin:ad-pixel</code>).
                </li>
                <li>Cloudflare may set a strictly necessary security cookie to tell people from attacking bots.</li>
              </ul>
              <p>
                <b>Reddit’s ad pixel.</b> A script from Reddit runs on the public pages and the sign-up page, never inside the app, and not in a browser that has signed in to {brand}. It sends Reddit the
                pages you view and whether you sign up, with your IP address and browser details, so Reddit can tell us which ads bring visitors, and it sets cookies whose names start with{' '}
                <code className="font-plex text-[0.9em]">_rdt_</code>. If Reddit’s automatic matching is on, it also sends a hashed (scrambled) form of an email address it finds on the page, such as the
                one you type to sign up, to match the visit to a Reddit account. Reddit uses this data under its own privacy policy, including to show you ads.
              </p>
              <p>
                In the EU, the European Economic Area, the UK and Switzerland, the website asks before the pixel runs. Elsewhere it runs unless you turn it off. You can change your choice at any time
                under “Your privacy choices” at the foot of the website, and turning it off deletes its cookies. A browser that sends Global Privacy Control never runs it.
              </p>
              <p>
                <b>Our own usage counts.</b> The website and the app send us the pages and screens you open and the links and buttons you click (see “What we collect, and why”). This sets no cookies and stores
                nothing in your browser, and the data goes to no one else.
              </p>
              <p>There are no other analytics, advertising or tracking cookies, and no other third-party scripts.</p>
            </>
          ),
        },
        {
          id: 'changes',
          title: 'Changes to this policy',
          body: <p>If we change this policy in a way that matters, we will email account owners at least 30 days before it applies, unless the law requires a faster change, and update the date above.</p>,
        },
      ]}
    />
  );
}

// --- Refund policy ------------------------------------------------------------------------------------------------

export function RefundsPage() {
  const { email, trialDays } = useParties();
  return (
    <LegalPage
      title="Refund and cancellation policy"
      intro={<p>Try it free first, cancel whenever you like, and get your money back if the first month isn’t right. Here is exactly how that works.</p>}
      sections={[
        { id: 'trial', title: 'The free trial', body: <p>The first {trialDays} days are free and need no card, so there is nothing to pay or refund for the trial.</p> },
        {
          id: 'cancel',
          title: 'Cancelling',
          body: (
            <p>
              An owner or admin can cancel at any time from Settings → Billing → Manage billing, which opens Dodo Payments’ billing portal, or by writing to {email}. There are no further charges. The company
              keeps full access until the end of the period already paid for, then becomes read-only: you can still sign in and export everything.
            </p>
          ),
        },
        {
          id: 'refunds',
          title: 'Refunds',
          body: (
            <ul>
              <li>
                <b>Your first payment:</b> if you ask within 14 days of your company’s first subscription payment, we refund it in full, no questions asked. This applies once per company.
              </li>
              <li>
                <b>Mistakes:</b> if you were charged twice, charged after cancelling, or charged the wrong amount, we refund the mistaken charge whenever you tell us.
              </li>
              <li>
                <b>If we fall short:</b> if we end the service, or change it so that it becomes substantially worse for you and you cancel, we refund the part of the period you paid for and didn’t get.
              </li>
              <li>
                <b>Otherwise:</b> renewals after the first payment aren’t refunded, and there are no partial refunds for unused days, but cancelling stops the next charge.
              </li>
            </ul>
          ),
        },
        {
          id: 'how',
          title: 'How to ask, and how it is paid',
          body: (
            <>
              <p>
                Write to {email} from your account’s email address, with the company name and the order or invoice number from Dodo Payments’ receipt. We reply within 2 business days.
              </p>
              <p>
                Approved refunds are issued through Dodo Payments, our merchant of record, to the original payment method. Banks usually show them within 5 to 10 business days. Refunding your first payment
                also ends the subscription.
              </p>
              <p>
                If your business is exempt from a sales tax that was charged, ask Dodo Payments for a tax refund within 60 days of the purchase, with a valid tax ID, as its{' '}
                <Ext href="https://dodopayments.com/legal/buyer-terms">Buyer Terms</Ext> describe.
              </p>
            </>
          ),
        },
        {
          id: 'disputes',
          title: 'Before disputing a charge',
          body: <p>Please write to us before asking your bank for a chargeback: we can usually fix it faster, and Dodo Payments’ Buyer Terms ask you to contact it before disputing a charge.</p>,
        },
      ]}
    />
  );
}

// --- Data processing terms ----------------------------------------------------------------------------------------

export function DpaPage() {
  const { brand, seller, address, email } = useParties();
  return (
    <LegalPage
      title="Data processing terms"
      intro={
        <>
          <p>
            These terms are part of the <A to="/terms">terms of service</A> between {seller} (“we”, the processor) and the business using {brand} (“you”, the controller). They apply when we process
            personal data in your company’s books and content for you (“customer personal data”), and meet Article 28 of the GDPR and UK GDPR, India’s DPDP Act (you are the data fiduciary, we are your
            data processor) and US state privacy laws (we are your service provider). Accepting the terms of service accepts these terms; no signature is needed.
          </p>
        </>
      }
      sections={[
        {
          id: 'details',
          title: 'What is processed',
          body: (
            <Rows
              rows={[
                ['Subject matter and purpose', 'Providing the service: storing, computing and showing your books, reports, the assistant, the API and webhooks, and supporting you.'],
                ['Duration', 'For as long as you use the service, and until deletion as set out below.'],
                ['People concerned', 'Your users; your customers, vendors and their contacts; employees and others named in your records and bank transactions.'],
                ['Kinds of data', 'Names, contact details, transaction and invoice details, bank transaction descriptions, and anything else you choose to record. No special categories are intended.'],
                ['Processing', 'Hosting, storage, backup, computation, AI-assisted analysis when you use the assistant, transmission to places you choose, and deletion.'],
              ]}
            />
          ),
        },
        {
          id: 'instructions',
          title: 'Our commitments',
          body: (
            <ul>
              <li>
                We process customer personal data only on your documented instructions: the terms, these data processing terms and how you use and configure the service. If we think an instruction breaks
                the law, we will tell you. If the law requires other processing, we will tell you first unless the law forbids it.
              </li>
              <li>Anyone we authorise to process customer personal data is bound to keep it confidential.</li>
              <li>We keep the security measures below, and review them as risks and technology change.</li>
              <li>
                We help you, as far as we reasonably can, to answer requests from the people concerned, to keep data secure, to handle breaches, and with impact assessments and consultations with authorities.
              </li>
              <li>We don’t sell customer personal data, share it for targeted advertising, use it outside the business purpose of providing the service, or combine it with other data, except as the law allows.</li>
            </ul>
          ),
        },
        {
          id: 'security',
          title: 'Security measures',
          body: (
            <ul>
              <li>Encryption in transit (HTTPS) and at rest by our database provider; hosting on providers with independent security audits.</li>
              <li>Passwords (salted scrypt), recovery codes, API keys and emailed links stored only as hashes; optional two-factor sign-in; rate limits on sign-in.</li>
              <li>Each company’s data separated by the service, roles inside each company, and approval of changes proposed by AI or API clients.</li>
              <li>An audit log of changes and a tamper-evident hash chain over posted entries.</li>
              <li>Backups with point-in-time restore; access to production limited to what running the service needs.</li>
            </ul>
          ),
        },
        {
          id: 'subprocessors',
          title: 'Subprocessors',
          body: (
            <>
              <p>
                You authorise us to use the subprocessors below. We bind each one by contract to data protection obligations at least as protective as these, and remain responsible for them. Before adding or
                replacing one, we update this list and the privacy policy and email your owners at least 30 days ahead. If you object on reasonable data protection grounds and we can’t address it, you may
                cancel, and we refund the part of the period you paid for and haven’t used.
              </p>
              <Subprocessors />
            </>
          ),
        },
        {
          id: 'breach',
          title: 'Personal data breaches',
          body: (
            <p>
              We tell you without undue delay, and within 48 hours of becoming aware of a breach affecting customer personal data, what happened, the data and people likely affected, the likely consequences
              and what we are doing about it, adding details as we learn them.
            </p>
          ),
        },
        {
          id: 'deletion',
          title: 'Return and deletion',
          body: (
            <p>
              You can export all of your company’s data at any time (Settings → Company). When an owner deletes the company, we delete customer personal data from the live service at once and from backups
              within 7 days, unless the law requires us to keep it. When the agreement otherwise ends, the terms of service and privacy policy describe how long it is kept.
            </p>
          ),
        },
        {
          id: 'audits',
          title: 'Information and audits',
          body: (
            <p>
              We make available the information reasonably needed to show we meet these terms, including our providers’ independent audit reports where they make them available. If an authority requires it,
              or that information isn’t enough, you may audit our compliance once a year, with 30 days’ notice, at your cost, in a way that protects other customers’ data and confidential information.
            </p>
          ),
        },
        {
          id: 'transfers',
          title: 'International transfers',
          body: (
            <>
              <p>
                We are in India and our subprocessors process data in the countries listed above. Where you are subject to the GDPR and the transfer to us needs safeguards, the{' '}
                <Ext href="https://eur-lex.europa.eu/eli/dec_impl/2021/914/oj">standard contractual clauses</Ext> adopted by the European Commission in Decision (EU) 2021/914 are incorporated by reference,
                with you as data exporter and us as data importer:
              </p>
              <ul>
                <li>Module Two (controller to processor), or Module Three where you are yourself a processor;</li>
                <li>clause 7 (docking) included; clause 9 option 2 (general authorisation, with the notice period above); the optional wording in clause 11 left out;</li>
                <li>clause 13: the supervisory authority of the EU member state where you are established, or where your representative is;</li>
                <li>clauses 17 and 18: the law and courts of Ireland;</li>
                <li>Annex I: the parties named here, and “What is processed” above; Annex II: the security measures above; Annex III: the subprocessors above.</li>
              </ul>
              <p>
                For data subject to the UK GDPR, the UK Information Commissioner’s International Data Transfer Addendum to those clauses (version B1.0) applies, with the details above. For Swiss data, the same
                clauses apply with references read as the Swiss Federal Act on Data Protection and the Federal Data Protection and Information Commissioner. If these clauses conflict with the rest of the
                agreement, the clauses win.
              </p>
            </>
          ),
        },
        {
          id: 'general',
          title: 'Liability and contact',
          body: (
            <p>
              Liability under these terms follows the terms of service, except where the standard contractual clauses or the law say otherwise. Questions about these terms: {seller}, {address}, {email}.
            </p>
          ),
        },
      ]}
    />
  );
}

// --- Contact ------------------------------------------------------------------------------------------------------

export function ContactPage() {
  const site = useSite().data;
  const { seller, address } = useParties();
  const mail = (subject: string) => (site ? `mailto:${site.supportEmail}?subject=${encodeURIComponent(subject)}` : undefined);
  const cards = [
    { title: 'Support', body: 'Questions about using RareMargin, importing your books, or something that isn’t working.', subject: 'Support', tint: 'bg-site-close' },
    { title: 'Billing', body: 'Subscriptions, invoices and refunds. Owners and admins can also manage billing in Settings → Billing.', subject: 'Billing', tint: 'bg-site-spend' },
    { title: 'Security', body: 'Report a vulnerability or a suspected account compromise. Please include the steps to reproduce.', subject: 'Security report', tint: 'bg-site-revenue' },
  ];
  return (
    <SiteLayout title={PAGES['/contact'].title}>
      <Band stage="soft" inner="!pt-16 lg:!pt-24">
        <div className="max-w-[760px]">
          <Eyebrow>Contact</Eyebrow>
          <h1 className="site-h1 mt-6">Talk to a person.</h1>
          <p className="site-lead mt-6 max-w-[52ch] text-site-ink-2">
            One address reaches the team: <span className="font-[600] text-site-ink">{site?.supportEmail ?? '…'}</span>. Include your company’s name so we can find your account. We reply within two
            business days.
          </p>
        </div>
        <div className="mt-14 grid gap-4 md:grid-cols-3">
          {cards.map((c) => (
            <div key={c.title} className={cx('flex flex-col rounded-[28px] p-7 sm:p-9', c.tint)}>
              <h2 className="site-h3">{c.title}</h2>
              <p className="mt-3 flex-1 text-[15.5px] leading-relaxed text-site-ink-2">{c.body}</p>
              {mail(c.subject) && (
                <SiteButton href={mail(c.subject)} variant="ink" arrow className="mt-7 self-start">
                  Email {c.title.toLowerCase()}
                </SiteButton>
              )}
            </div>
          ))}
        </div>
        <dl className="mt-14 grid max-w-[760px] border-t-2 border-site-green text-[15.5px]">
          {[
            ['Business', site?.businessName ?? '…'],
            ['Run by', seller],
            ['Address', address],
            ['Email', site?.supportEmail ?? '…'],
            [
              'Grievances',
              <>
                {seller} is the Grievance Officer for privacy complaints: write to {site?.supportEmail ?? 'the address above'}. See the <A to="/privacy#grievance">privacy policy</A>.
              </>,
            ],
          ].map(([k, v]) => (
            <div key={String(k)} className="grid grid-cols-[120px_1fr] gap-4 border-b border-site-rule py-4">
              <dt className="site-eyebrow pt-1 text-site-muted">{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      </Band>
    </SiteLayout>
  );
}
